import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPostBySlug, getRelatedPosts } from '../services/wordpress';
import { NewsItem } from '../types';
import { safeHTML, stripHTML } from '../utils/safeHTML';
import { 
  Calendar, 
  User, 
  ArrowLeft, 
  Loader2,
  Share2, 
  Facebook, 
  Twitter, 
  Link as LinkIcon, 
  MessageCircle,
  Download,
  FileText,
  Lock
} from 'lucide-react';
import { useToast } from './Toast';
import './SinglePost.css';

const SinglePost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [post, setPost] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState<NewsItem[]>([]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    async function loadPost() {
      if (!slug) return;
      setLoading(true);
      setRelatedPosts([]);
      try {
        const data = await getPostBySlug(slug);
        setPost(data);

        // Jika URL saat ini berupa ID angka dan postingan memiliki slug teks yang valid, ubah URL ke slug resminya
        if (data?.slug && data.slug !== slug && !/^\d+$/.test(data.slug) && /^\d+$/.test(slug)) {
          navigate(`/${data.slug}`, { replace: true });
        }

        if (data?.category) {
          const related = await getRelatedPosts(data.category, data.id, 3, data.tags);
          setRelatedPosts(related);
        }
      } catch (err) {
        console.error("Error loading post:", err);
      } finally {
        setLoading(false);
      }
    }
    loadPost();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleShare = (platform: 'fb' | 'tw' | 'wa' | 'copy') => {
    if (!post || !slug) return;
    
    const shareUrl = `${window.location.origin}/${post.slug || slug}`;
    const title = post.title.replace(/<[^>]+>/g, '').trim();
    
    switch (platform) {
      case 'fb': window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer'); break;
      case 'tw': window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`, '_blank', 'noopener,noreferrer'); break;
      case 'wa': window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + '\n\n' + shareUrl)}`, '_blank', 'noopener,noreferrer'); break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
        break;
    }
  };

  const handleDirectDownload = async (url: string) => {
    if (!url || url === '#') { showToast('File PDF tidak tersedia.', 'error'); return; }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${post?.title.replace(/<[^>]+>/g, '').trim() ?? 'dokumen'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="SinglePost_loadingState" role="status">
        <Loader2 className="SinglePost_spinner" size={48} />
        <span className="sr-only">Memuat artikel...</span>
      </div>
    );
  }

  if (!post || !slug) return (
      <div className="SinglePost_errorState">
          <ShieldAlert className="SinglePost_errorIcon" size={64} />
          <h2 className="SinglePost_errorTitle">Konten Tidak Ditemukan</h2>
          <p className="SinglePost_errorText">Maaf, artikel atau publikasi dengan ID ini tidak tersedia di database.</p>
          <button onClick={() => navigate(-1)} className="SinglePost_errorBtn">Kembali</button>
      </div>
  );

  const isPublication = !!(post.pdfUrl && post.pdfUrl !== '' && post.pdfUrl !== '#');
  const postSlug = post.slug || slug || post.id;
  const canonicalUrl = `https://lakpesdam.or.id/${postSlug}`;

  return (
    <article className="SinglePost_page">
      <Helmet>
        <title>{stripHTML(post.title)} - Lakpesdam PBNU</title>
        <meta name="description" content={stripHTML(post.excerpt).substring(0, 160) + '...'} />
        <meta property="og:title" content={stripHTML(post.title)} />
        <meta property="og:description" content={stripHTML(post.excerpt).substring(0, 160) + '...'} />
        <meta property="og:url" content={canonicalUrl} />
        {post.imageUrl && <meta property="og:image" content={post.imageUrl} />}
        {!post.imageUrl && <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={stripHTML(post.title)} />
        <meta name="twitter:description" content={stripHTML(post.excerpt).substring(0, 160) + '...'} />
        {post.imageUrl && <meta name="twitter:image" content={post.imageUrl} />}
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="id" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": stripHTML(post.title),
            "description": stripHTML(post.excerpt).substring(0, 160),
            "datePublished": post.date,
            "author": post.author_name ? { "@type": "Person", "name": post.author_name } : undefined,
            "publisher": { "@type": "Organization", "name": "Lakpesdam PBNU", "logo": "https://lakpesdam.or.id/logo.png" },
            "image": post.imageUrl || undefined,
            "mainEntityOfPage": canonicalUrl
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Beranda", "item": "https://lakpesdam.or.id/" },
              { "@type": "ListItem", "position": 2, "name": post.category, "item": "https://lakpesdam.or.id/berita" },
              { "@type": "ListItem", "position": 3, "name": stripHTML(post.title) }
            ]
          })}
        </script>
      </Helmet>
      <div className="SinglePost_container">
        
        <button onClick={() => navigate(-1)} className="SinglePost_backBtn mb-8">
          <ArrowLeft size={20} /> Kembali
        </button>

        <header className="SinglePost_header">
          <span className="SinglePost_category">{post.category}</span>
          {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
          <h1 className="SinglePost_title" {...safeHTML(post.title)} aria-label={post.title.replace(/<[^>]+>/g, '').trim()} />
          
          <div className="SinglePost_meta">
            <div className="SinglePost_metaItem">
              <Calendar size={16} />
              <span>{post.date}</span>
            </div>
            {post.author_name && (
              <div className="SinglePost_metaItem">
                <User size={16} />
                <span>{post.author_name}</span>
              </div>
            )}
          </div>
        </header>

        {/* Featured Image dengan Fallback Placeholder */}
        <div className="SinglePost_featuredImageWrapper">
          {!imgError && post.imageUrl ? (
            <img 
              src={post.imageUrl} 
              alt={post.title} 
              className="SinglePost_featuredImage" 
              loading="lazy"
              width="800"
              height="400"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-emerald-800 to-emerald-950 text-white p-8 text-center">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm border border-white/20">
                <span className="text-4xl font-bold text-amber-500">L</span>
              </div>
              <p className="text-emerald-100 font-medium max-w-xs">{post.title.replace(/<[^>]+>/g, '').trim()}</p>
            </div>
          )}
        </div>

        {isPublication && post.pdfUrl && (
            <div className="SinglePost_pdfSection">
                <div className="SinglePost_pdfHeader">
                    <div className="flex items-center gap-3">
                        <div className="SinglePost_pdfIcon"><FileText size={28} /></div>
                        <div>
                            <h4 className="SinglePost_pdfTitle">Dokumen Publikasi</h4>
                            <p className="SinglePost_pdfMeta">{post.type || 'PDF Dokumen'}</p>
                        </div>
                    </div>
                    {post.is_downloadable !== false && (
                        <button
                            onClick={() => handleDirectDownload(post.pdfUrl!)}
                            className="SinglePost_pdfDownloadBtn"
                            title="Unduh PDF"
                        >
                            <Download size={16} /> Unduh PDF
                        </button>
                    )}
                    {post.is_downloadable === false && (
                        <div className="SinglePost_pdfLocked">
                            <Lock size={14} /> Hanya Lihat
                        </div>
                    )}
                </div>
                {isMobile ? (
                    <div className="SinglePost_pdfMobileMsg">
                        <FileText size={36} className="SinglePost_pdfMobileIcon" />
                        <p>Dokumen PDF tersedia. Gunakan perangkat desktop untuk tampilan optimal.</p>
                        <a
                            href={post.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="SinglePost_pdfMobileLink"
                        >
                            Buka di Tab Baru
                        </a>
                    </div>
                ) : (
                    <iframe
                        src={`${post.pdfUrl}${post.is_downloadable === false ? '#toolbar=0&navpanes=0&scrollbar=1' : '#toolbar=1'}`}
                        className="SinglePost_pdfFrame"
                        title={`Dokumen: ${post.title.replace(/<[^>]+>/g, '').trim()}`}
                    />
                )}
            </div>
        )}

        <ArticleContentWithRelated
          htmlContent={post.content || post.excerpt || ''}
          relatedPosts={relatedPosts}
        />

        <div className="SinglePost_shareSection">
          <div className="SinglePost_shareTitle"><Share2 size={18} /><span>Bagikan Artikel</span></div>
          <div className="SinglePost_shareButtons">
            <button onClick={() => handleShare('wa')} aria-label="Bagikan ke WhatsApp" className="SinglePost_shareBtn SinglePost_wa"><MessageCircle size={20} /></button>
            <button onClick={() => handleShare('fb')} aria-label="Bagikan ke Facebook" className="SinglePost_shareBtn SinglePost_fb"><Facebook size={20} /></button>
            <button onClick={() => handleShare('tw')} aria-label="Bagikan ke Twitter" className="SinglePost_shareBtn SinglePost_tw"><Twitter size={20} /></button>
            <button onClick={() => handleShare('copy')} aria-label="Salin tautan" className="SinglePost_shareBtn SinglePost_copy">
              <LinkIcon size={20} />
              {copied && <span className="SinglePost_copyTooltip">Tautan Disalin!</span>}
            </button>
          </div>
        </div>

        <div className="SinglePost_footer">
           <button onClick={() => navigate(-1)} className="SinglePost_backBtn"><ArrowLeft size={20} /> Kembali</button>
        </div>

      </div>
    </article>
  );
};

// Sub-component minimal untuk icon error
const ShieldAlert = ({ className, size }: { className: string, size: number }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
    </svg>
);

/**
 * Memecah konten HTML artikel menjadi dua bagian dan menyisipkan
 * blok "Berita Terkait" di antaranya.
 */
const ArticleContentWithRelated: React.FC<{
  htmlContent: string;
  relatedPosts: NewsItem[];
}> = ({ htmlContent, relatedPosts }) => {
  const { contentBefore, contentAfter } = useMemo(() => {
    if (!htmlContent || relatedPosts.length === 0) {
      return { contentBefore: htmlContent, contentAfter: '' };
    }

    // Pecah berdasarkan tag penutup paragraf </p>
    const paragraphs = htmlContent.split(/<\/p>/i);
    // Hanya sisipkan jika artikel cukup panjang (minimal 4 paragraf)
    if (paragraphs.length < 4) {
      return { contentBefore: htmlContent, contentAfter: '' };
    }

    // Sisipkan di sekitar 40% konten
    const splitIndex = Math.max(2, Math.floor(paragraphs.length * 0.4));
    const before = paragraphs.slice(0, splitIndex).join('</p>') + '</p>';
    const after = paragraphs.slice(splitIndex).join('</p>');

    return { contentBefore: before, contentAfter: after };
  }, [htmlContent, relatedPosts]);

  return (
    <>
      <div className="SinglePost_content" {...safeHTML(contentBefore)} />

      {relatedPosts.length > 0 && contentAfter && (
        <aside className="SinglePost_relatedBlock" aria-label="Berita Terkait">
          <div className="SinglePost_relatedAccent" />
          <div className="SinglePost_relatedInner">
            <h3 className="SinglePost_relatedTitle">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
                <path d="M18 14h-8" /><path d="M15 18h-5" />
                <path d="M10 6h8v4h-8V6Z" />
              </svg>
              Berita Terkait
            </h3>
            <div className="SinglePost_relatedGrid">
              {relatedPosts.map((item) => (
                <Link
                  key={item.id}
                  to={`/\${item.slug || item.id}`}
                  className="SinglePost_relatedCard"
                >
                  <div className="SinglePost_relatedImgWrap">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={stripHTML(item.title)}
                        className="SinglePost_relatedImg"
                        loading="lazy"
                        width="280"
                        height="160"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="SinglePost_relatedImgPlaceholder">
                        <span>L</span>
                      </div>
                    )}
                  </div>
                  <div className="SinglePost_relatedBody">
                    <span className="SinglePost_relatedCategory">{item.category}</span>
                    {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                    <h4
                      className="SinglePost_relatedCardTitle"
                      {...safeHTML(item.title)}
                    />
                    <span className="SinglePost_relatedDate">{item.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      )}

      {contentAfter && (
        <div className="SinglePost_content" {...safeHTML(contentAfter)} />
      )}
    </>
  );
};

export default SinglePost;

